<script lang="ts">
  import { onMount } from 'svelte';
  import { animate, spring } from '@motionone/dom';

  export let images: string[] = [];
  export let autoplay: boolean = true;
  export let interval: number = 3000;
  export let showControls: boolean = true;

  let currentPage = 0;
  let container: HTMLElement;
  let isAnimating = false;

  function nextPage() {
    if (isAnimating || currentPage >= images.length - 1) return;
    isAnimating = true;
    
    animate(
      container,
      { 
        x: ['0%', '-100%'],
        opacity: [1, 0.8]
      },
      { 
        duration: 0.8,
        easing: spring({ damping: 25, stiffness: 200 })
      }
    ).finished.then(() => {
      currentPage++;
      container.style.transform = 'translateX(100%)';
      container.style.opacity = '0.8';
      
      requestAnimationFrame(() => {
        animate(
          container,
          { 
            x: ['100%', '0%'],
            opacity: [0.8, 1]
          },
          { 
            duration: 0.8,
            easing: spring({ damping: 25, stiffness: 200 })
          }
        ).finished.then(() => {
          isAnimating = false;
        });
      });
    });
  }

  function previousPage() {
    if (isAnimating || currentPage <= 0) return;
    isAnimating = true;
    
    animate(
      container,
      { 
        x: ['0%', '100%'],
        opacity: [1, 0.8]
      },
      { 
        duration: 0.8,
        easing: spring({ damping: 25, stiffness: 200 })
      }
    ).finished.then(() => {
      currentPage--;
      container.style.transform = 'translateX(-100%)';
      container.style.opacity = '0.8';
      
      requestAnimationFrame(() => {
        animate(
          container,
          { 
            x: ['-100%', '0%'],
            opacity: [0.8, 1]
          },
          { 
            duration: 0.8,
            easing: spring({ damping: 25, stiffness: 200 })
          }
        ).finished.then(() => {
          isAnimating = false;
        });
      });
    });
  }

  let autoplayInterval: number;

  onMount(() => {
    if (autoplay) {
      autoplayInterval = setInterval(() => {
        if (!isAnimating) {
          nextPage();
        }
      }, interval);
    }

    return () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
      }
    };
  });
</script>

<div class="pdf-viewer relative w-full bg-white rounded-lg shadow-xl overflow-hidden">
  <div 
    bind:this={container}
    class="absolute inset-0 w-full h-full"
    style="will-change: transform, opacity;"
  >
    <img 
      src={images[currentPage]} 
      alt="Page {currentPage + 1}" 
      class="w-full h-full object-contain"
    />
  </div>

  {#if showControls}
    <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
      <button 
        on:click={previousPage}
        class="p-2 hover:bg-black/5 rounded-full transition-colors disabled:opacity-50"
        disabled={currentPage === 0 || isAnimating}
      >
        ←
      </button>
      <span class="text-sm">
        {currentPage + 1} / {images.length}
      </span>
      <button 
        on:click={nextPage}
        class="p-2 hover:bg-black/5 rounded-full transition-colors disabled:opacity-50"
        disabled={currentPage === images.length - 1 || isAnimating}
      >
        →
      </button>
    </div>
  {/if}
</div>

<style>
  .pdf-viewer {
    perspective: 1000px;
    aspect-ratio: 1240/1754;
  }
</style> 